import { Contact } from '../types'
import {
  Button,
  focusedBoxShadow,
  GreyGrey60,
  IconPlus,
  InputSelectControlField,
  Primary13SemiBold,
  Section,
  SequenceMenu,
  SwitchLineField,
  TextInputField,
  Box,
  Flex,
  Text
} from '@sequencehq/core-components'
import { ValidationError, ValidatorWithMetadata } from '@sequencehq/utils'
import { useEffect } from 'react'
import { isEmail, required } from '@sequencehq/utils/validation'
import {
  assignContactPurpose,
  isPrimaryBillingContact,
  receivesBillingNotifications,
  setBillingNotifications
} from 'CustomerContacts/utils/contactPurposes'

const inputStyles = {
  paddingLeft: '12px'
}
const fieldStyles = {
  label: {
    ...Primary13SemiBold,
    color: GreyGrey60
  }
}

export const contactsHaveValidEmails: ValidatorWithMetadata<{
  contactId: string
  field: string
}> = (value: unknown) => {
  if (!Array.isArray(value)) return []

  return value
    .map(
      contact =>
        isEmail(contact.email) && {
          message: 'Please enter a valid email address',
          metadata: {
            contactId: contact.id,
            field: 'email'
          }
        }
    )
    .filter(
      (
        err
      ): err is {
        message: string
        metadata: { contactId: string; field: string }
      } => Boolean(err)
    )
}

export const contactsHaveValidNames: ValidatorWithMetadata<{
  contactId: string
  field: string
}> = (value: unknown) => {
  if (!Array.isArray(value)) return []

  return value
    .map(
      contact =>
        required(contact.name) && {
          message: 'Please enter a valid name',
          metadata: {
            contactId: contact.id,
            field: 'name'
          }
        }
    )
    .filter(
      (
        err
      ): err is {
        message: string
        metadata: { contactId: string; field: string }
      } => Boolean(err)
    )
}

export const contactsMustHavePrimaryContact: ValidatorWithMetadata<{
  field: string
}> = (value: unknown) => {
  if (!Array.isArray(value)) return []

  const hasPrimary = value.some(isPrimaryBillingContact)
  if (hasPrimary) return []

  return [
    {
      message: 'Please select a primary contact',
      metadata: {
        field: 'primaryContact'
      }
    }
  ]
}

/**
 * Validators for the contacts field. The flags mirror the form-level props for
 * a form that shows only a subset of a larger contact roster: with
 * `requirePrimaryContact` off, having no primary among the shown contacts is a
 * valid state (the roster's primary isn't shown); with `requireContacts` off,
 * an empty list is valid (unshown contacts keep the roster non-empty).
 */
export const getCustomerContactsValidators = ({
  requirePrimaryContact,
  requireContacts
}: {
  requirePrimaryContact: boolean
  requireContacts: boolean
}) => [
  ...(requireContacts ? [required] : []),
  contactsHaveValidEmails,
  contactsHaveValidNames,
  ...(requirePrimaryContact ? [contactsMustHavePrimaryContact] : [])
]

export const isValidContact = (contact: Contact): boolean =>
  contact.name.trim() !== '' && contact.email.trim() !== ''

export const CustomerContactsField = (props: {
  value: Array<Contact>
  onChange: (newValue: Array<Contact>) => void
  validationErrors: Array<ValidationError>
  'data-testid'?: string
  /**
   * When true, each contact gets a toggle for whether it receives invoice
   * notifications/reminders. The primary contact's toggle is
   * locked on. Defaults to false.
   */
  showInvoiceRecipientToggle?: boolean
  /**
   * When false, the contacts shown are a subset of a larger roster whose
   * primary contact may not be among them: no contact is auto-promoted to
   * primary, and the primary selector only renders when a shown contact
   * already holds the role. Defaults to true.
   */
  requirePrimaryContact?: boolean
  /**
   * Index of the contact card to ring as active (e.g. matching a focused field
   * in a side-by-side review). The ringed card also carries
   * `data-active-contact` so its active state is assertable without depending on
   * the styling. When undefined, no card is ringed.
   */
  activeContactIndex?: number
  /** Heading shown above the contact cards, below the primary selector. */
  label?: string
}) => {
  const requirePrimaryContact = props.requirePrimaryContact ?? true

  const primaryContactValidationErrors = props.validationErrors.filter(
    ve => ve.metadata?.field === 'primaryContact'
  )

  const validContacts = props.value.filter(isValidContact)
  const currentPrimary = props.value.find(isPrimaryBillingContact)
  const currentPrimaryIsValid = Boolean(
    currentPrimary && isValidContact(currentPrimary)
  )

  useEffect(() => {
    // With `requirePrimaryContact` off, no shown primary is a valid state (the
    // roster's primary may not be shown), so never promote one.
    if (!requirePrimaryContact || validContacts.length === 0) {
      return
    }

    const needsNewPrimary = !currentPrimary || !currentPrimaryIsValid

    if (needsNewPrimary) {
      props.onChange(
        assignContactPurpose(props.value, validContacts[0].id, 'BILLING')
      )
    }
  }, [
    validContacts.length,
    currentPrimary?.id,
    currentPrimaryIsValid,
    requirePrimaryContact
  ])

  // With `requirePrimaryContact` off and no shown contact holding the role, the
  // primary lives outside this subset — offering the selector would move the
  // role onto a shown contact, leaving the roster with two primaries.
  const showPrimaryContactSelector =
    (props.value.length > 1 &&
      (requirePrimaryContact || Boolean(currentPrimary))) ||
    primaryContactValidationErrors.length > 0

  return (
    <Flex direction="column" gap="12px">
      {showPrimaryContactSelector && (
        <InputSelectControlField
          data-testid={
            props['data-testid']
              ? `${props['data-testid']}.primaryContact`
              : undefined
          }
          label="Primary contact"
          disabled={validContacts.length === 0}
          value={currentPrimaryIsValid ? (currentPrimary?.id ?? '') : ''}
          onChange={newContactId => {
            props.onChange(
              assignContactPurpose(props.value, newContactId, 'BILLING')
            )
          }}
          validationErrors={primaryContactValidationErrors}
          options={validContacts.map(contact => ({
            value: contact.id,
            label: contact.name,
            secondaryLabel: contact.email
          }))}
          placeholder="Select primary contact"
          styles={fieldStyles}
        />
      )}
      <Flex direction="column" gap="8px">
        {props.label && <Text {...fieldStyles.label}>{props.label}</Text>}
        {props.value.length > 0 && (
          <Flex direction="column" gap="8px">
            {props.value.map((contact, contactIdx) => {
              // Ring the card being edited; a box-shadow (not a border) avoids
              // layout shift as the ring moves between cards.
              const isActiveContact = contactIdx === props.activeContactIndex

              return (
                <Section
                  variant="emphasized"
                  key={contact.id}
                  padding="16px"
                  position="relative"
                  display={'flex'}
                  flexDirection="column"
                  gap="8px"
                  data-contact-index={contactIdx}
                  data-active-contact={isActiveContact || undefined}
                  boxShadow={isActiveContact ? focusedBoxShadow : undefined}
                >
                  {props.value.length > 1 && (
                    <Box position="absolute" top="8px" right="16px">
                      <SequenceMenu
                        variant="ghost"
                        data-testid={
                          props['data-testid']
                            ? `${props['data-testid']}.contact.${contactIdx}.menu`
                            : undefined
                        }
                        buttonSize="small"
                        alignment="bottom-right"
                        items={[
                          ({ setIsOpen }) => (
                            <SequenceMenu.Item
                              key="Remove contact"
                              uuid="remove-contact"
                              label="Remove contact"
                              data-testid={
                                props['data-testid']
                                  ? `${props['data-testid']}.contact.${contactIdx}.menu.remove`
                                  : undefined
                              }
                              onClick={() => {
                                const deletedContactIsPrimary =
                                  isPrimaryBillingContact(contact)
                                const newValue = props.value.filter(
                                  c => c.id !== contact.id
                                )
                                if (
                                  deletedContactIsPrimary &&
                                  newValue.length
                                ) {
                                  // Prefer promoting a contact that already receives
                                  // invoices, so an explicitly-off contact isn't
                                  // forced back on unless it's the only option.
                                  const newPrimary =
                                    newValue.find(
                                      receivesBillingNotifications
                                    ) ?? newValue[0]
                                  props.onChange(
                                    assignContactPurpose(
                                      newValue,
                                      newPrimary.id,
                                      'BILLING'
                                    )
                                  )
                                } else {
                                  props.onChange(newValue)
                                }
                                setIsOpen?.(false)
                              }}
                              destructive
                            />
                          )
                        ]}
                      />
                    </Box>
                  )}
                  <TextInputField
                    data-testid={
                      props['data-testid']
                        ? `${props['data-testid']}.contact.${contactIdx}.name`
                        : undefined
                    }
                    label="Full name"
                    placeholder="Full name"
                    value={contact.name}
                    onChange={e =>
                      props.onChange(
                        props.value.map(c =>
                          c.id === contact.id
                            ? { ...c, name: e.target.value }
                            : c
                        )
                      )
                    }
                    validationErrors={props.validationErrors?.filter(
                      ve =>
                        ve.metadata?.contactId === contact.id &&
                        ve.metadata?.field === 'name'
                    )}
                    {...inputStyles}
                    styles={fieldStyles}
                  />
                  <TextInputField
                    data-testid={
                      props['data-testid']
                        ? `${props['data-testid']}.contact.${contactIdx}.email`
                        : undefined
                    }
                    label="Email"
                    placeholder="Email"
                    value={contact.email}
                    onChange={e =>
                      props.onChange(
                        props.value.map(c =>
                          c.id === contact.id
                            ? { ...c, email: e.target.value }
                            : c
                        )
                      )
                    }
                    validationErrors={props.validationErrors?.filter(
                      ve =>
                        ve.metadata?.contactId === contact.id &&
                        ve.metadata?.field === 'email'
                    )}
                    {...inputStyles}
                    styles={fieldStyles}
                  />
                  {props.showInvoiceRecipientToggle && (
                    <SwitchLineField
                      data-testid={
                        props['data-testid']
                          ? `${props['data-testid']}.contact.${contactIdx}.receivesInvoices`
                          : undefined
                      }
                      text="Send invoice notifications and payment reminders to this recipient"
                      // The primary contact must receive invoices, so its toggle is
                      // locked on; switch the primary via the selector above instead.
                      isDisabled={isPrimaryBillingContact(contact)}
                      value={receivesBillingNotifications(contact)}
                      onChange={checked =>
                        props.onChange(
                          props.value.map(c =>
                            c.id === contact.id
                              ? setBillingNotifications(c, checked)
                              : c
                          )
                        )
                      }
                      styles={{
                        wrapper: { marginTop: '8px' }
                      }}
                    />
                  )}
                </Section>
              )
            })}
          </Flex>
        )}
        <Button
          variant="ghost"
          fullWidth
          leadingIcon={<IconPlus />}
          data-testid={
            props['data-testid']
              ? `${props['data-testid']}.addContact`
              : undefined
          }
          onClick={() => {
            // A first contact only defaults to primary when the form owns
            // the whole roster; a subset's primary may live among the
            // unshown contacts.
            const becomesPrimary =
              requirePrimaryContact && props.value.length === 0
            props.onChange([
              ...props.value,
              {
                id: crypto.randomUUID(),
                name: '',
                email: '',
                purposes: becomesPrimary ? ['BILLING'] : [],
                notificationSubscriptions: ['BILLING']
              }
            ])
          }}
        >
          Add contact
        </Button>
      </Flex>
    </Flex>
  )
}
